import React from "react"
import { Icon } from "../icon"
import { Button } from "../button"
import { Tooltip } from "../tooltip"
import { VisuallyHidden } from "../visually-hidden"
import { Telemetry } from "../event-emitter"
import { ColorTile } from "./color-tile"

export type SavedColors = {
  colors: string[]
  handleColorSelect?: (color: string) => void
  handleColorsEdit?: () => void
  handleColorAdd?: (color: string) => void
}

export type SavedColorsSectionProps = {
  /** Array of saved color hex strings */
  colors?: string[]
  /** Called when a saved color tile is clicked */
  handleColorSelect: (color: string) => void
  /** Called when the edit/settings button is clicked; omit to hide the icon */
  handleColorsEdit?: () => void
  /** Called with the current color when the add (+) tile is clicked */
  handleColorAdd?: (color: string) => void
  /** The currently selected color, offered by the add (+) tile */
  currentColor?: string
}

const MAX_COLOR_ARRAY_LENGTH = 12

function SettingsTile({ handleColorsEdit }: { handleColorsEdit: () => void }) {
  return (
    <Tooltip decorative>
      <Tooltip.Trigger
        render={
          <Button
            variant="tertiary"
            size="small"
            telemetry={{
              name: "color_settings",
              source: "color_picker",
              action: "open_settings"
            }}
            onClick={handleColorsEdit}
          >
            <Icon name="settings-sliders" size="xs" />
            <VisuallyHidden>Edit saved colors</VisuallyHidden>
          </Button>
        }
      />
      <Tooltip.Content position="top">Edit saved colors</Tooltip.Content>
    </Tooltip>
  )
}

type AddColorTileProps = {
  currentColor: string
  handleColorAdd: (color: string) => void
}

function AddColorTile({ currentColor, handleColorAdd }: AddColorTileProps) {
  return (
    <Tooltip decorative>
      <Tooltip.Trigger
        render={
          <Button
            variant="tertiary"
            size="small"
            telemetry={{ action: "add" }}
            onClick={() => handleColorAdd(currentColor)}
          >
            <Icon name="plus" size="xs" />
            <VisuallyHidden>Add current color to saved colors</VisuallyHidden>
          </Button>
        }
      />
      <Tooltip.Content position="top">
        Add color to saved colors
      </Tooltip.Content>
    </Tooltip>
  )
}

type SavedColorTilesProps = {
  colors: string[]
  handleColorSelect: (color: string) => void
  handleColorAdd?: (color: string) => void
  currentColor?: string
  showAddTile: boolean
}

function SavedColorTiles({
  colors,
  handleColorSelect,
  handleColorAdd,
  currentColor,
  showAddTile
}: SavedColorTilesProps) {
  const tiles = colors.map((color, index) => {
    return (
      <ColorTile
        color={color}
        key={index}
        handleColorSelect={handleColorSelect}
      />
    )
  })

  return (
    <Telemetry value={{ name: "color_tile", source: "saved_colors" }}>
      <div className="grid grid-cols-6 gap-2">
        {tiles}
        {showAddTile && handleColorAdd && currentColor && (
          <AddColorTile
            currentColor={currentColor}
            handleColorAdd={handleColorAdd}
          />
        )}
      </div>
    </Telemetry>
  )
}

export function SavedColorsSection({
  colors = [],
  handleColorSelect,
  handleColorsEdit,
  handleColorAdd,
  currentColor
}: SavedColorsSectionProps) {
  // Shared by the "+" tile and the "Click +" hint so the two always agree.
  const showAddTile = Boolean(
    handleColorAdd &&
      currentColor &&
      currentColor !== "transparent" &&
      !colors.includes(currentColor) &&
      colors.length < MAX_COLOR_ARRAY_LENGTH
  )
  const title =
    colors.length || !showAddTile
      ? "Saved colors"
      : "Click + to add saved colors"
  return (
    <div className="border-t w-auto mt-4 -mx-3">
      <div className="pl-3 mr-3 w-auto">
        <div className="flex items-center justify-between mt-3 mb-2">
          <div className="text-gray-800 text-xs font-semibold">{title}</div>
          {handleColorsEdit && (
            <SettingsTile handleColorsEdit={handleColorsEdit} />
          )}
        </div>
        <SavedColorTiles
          colors={colors}
          handleColorSelect={handleColorSelect}
          handleColorAdd={handleColorAdd}
          currentColor={currentColor}
          showAddTile={showAddTile}
        />
      </div>
    </div>
  )
}
